import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import type { FormatHookParams } from '/business/shared/components/pages/question/types';

import useHowWasCollected from '/business/shared/components/pages/question/formats/staticSharedFields/useHowWasCollected';
import useMakeSupplierName from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeSupplierName';
import {
  QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
  QUESTION_BRANCH_GROUP_KEY_INTENSITY,
} from '/business/shared/components/pages/question/questionEnums';
import Button from '/shared/components/widgets/button/Button';
import WidgetAccordion from '/shared/components/widgets/widget-accordion/WidgetAccordion';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import WidgetIcon from '/business/shared/images/icons/expander-icon.svg';
import useAbsoluteEmissions from '/business/shared/components/pages/question/formats/staticSharedFields/useAbsoluteEmissions';
import useMakeIntensity from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeIntensity';
import useMakeUnit from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeUnit';
import useMakeQuantity from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeQuantity';
import {
  useGetQuantityDropdownValues,
  useGetUnitDropdownValues,
} from '/business/shared/components/pages/question/useFieldUnits';

export default function useFormatUpstreamTransportationCustom({
  question,
  questionFormatName,
  branchType,
  label,
  emissionSourcePlaceholder,
}: FormatHookParams) {
  const supplierName = useMakeSupplierName({});
  const intensity = useMakeIntensity();
  const unit = useMakeUnit();
  const quantity = useMakeQuantity({
    subLabel: 'E.g. for 100 litres, enter 100',
  });
  const quantityWithDropdown = useMakeQuantity({
    quantityDropdownValues: useGetQuantityDropdownValues({ questionFormatName }),
    subLabel: 'E.g. for 100 litres, enter 100',
  });

  const absoluteEmissions = useAbsoluteEmissions({
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(true),
  });
  const howWasCollected = useHowWasCollected();

  const pageSchema = useMemo(() => {
    return [
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Intensity value/unit
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data based on carbon intensity/unit (e.g., kg CO₂e per unit of product).
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Absolute emissions
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data as a total sum (total kg or tonnes CO₂e) in relation to products or services your
                  business used.
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...supplierName,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...intensity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...unit,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...quantity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...howWasCollected,
      },

      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...supplierName,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...absoluteEmissions,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...quantityWithDropdown,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...howWasCollected,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_INTENSITY}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_INTENSITY);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_ABSOLUTE}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_ABSOLUTE);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
    ];
  }, [label, emissionSourcePlaceholder]);

  const [saveCallback, setSaveCallback] = useState<null | 'saveNew'>(null);
  const [questionGroupForValidation, setQuestionGroupForValidation] = useState<null | string>(null);

  return {
    pageSchema,
    saveCallback,
    questionGroupForValidation,
  };
}
